import {Alert, Button, Table, Tag} from "antd";
import {ColumnsType} from "antd/es/table";
import moment from "moment";
import {useEffect, useMemo, useState} from "react";
import {StyledDescriptionText} from "../../../../components/calls/upcoming-calls/upcoming-calls.style";
import {
  DiscountTransaction,
  useGetDiscountTransactions,
} from "../../../../react-query-hooks/discounts/discounts";
import {formatCurrencyAmount} from "../../../../utils/format-currency-amount";

type OfferingTypeLabel = "1:1 sessions" | "Live events" | "Products" | "Services";

interface UsageRow {
  key: string;
  customer: string;
  email: string;
  type: OfferingTypeLabel;
  offering: string;
  date: string;
  amount: string;
}

const OFFERING_TYPE_TAG_COLORS: Record<OfferingTypeLabel, string> = {
  "1:1 sessions": "magenta",
  "Live events": "gold",
  Products: "purple",
  Services: "blue",
};

function toTitleCase(value: string) {
  return value
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getOfferingTypeLabel(offeringType: string): OfferingTypeLabel {
  switch (offeringType) {
    case "PRODUCT":
      return "Products";
    case "LIVE_EVENT":
      return "Live events";
    case "SERVICE":
    case "SERVICES":
      return "Services";
    case "SESSION":
    case "ONE_ON_ONE_SESSION":
    case "ONE_TO_ONE_SESSION":
      return "1:1 sessions";
    default:
      return toTitleCase(offeringType.replace(/_/g, " ")) as OfferingTypeLabel;
  }
}

export function DiscountUsageDetail({discountId}: {discountId: number}) {
  const [page, setPage] = useState(1);
  const perPage = 10;
  const {data, error, isLoading, isFetching} = useGetDiscountTransactions(
    discountId,
    page,
    perPage
  );

  useEffect(() => {
    setPage(1);
  }, [discountId]);

  const tableData: UsageRow[] = useMemo(
    () =>
      (data?.transactions ?? []).map((transaction: DiscountTransaction, index) => ({
        key: `${transaction.email}-${transaction.purchaseDate}-${index}`,
        customer: transaction.customerName,
        email: transaction.email,
        type: getOfferingTypeLabel(transaction.offeringType),
        offering: transaction.offeringName,
        date: transaction.purchaseDate,
        amount: formatCurrencyAmount(transaction.amount, transaction.currency),
      })),
    [data?.transactions]
  );

  const columns: ColumnsType<UsageRow> = [
    {
      title: "Customer name",
      dataIndex: "customer",
      key: "customer",
      width: 180,
      ellipsis: true,
      render: (value) => <StyledDescriptionText>{value}</StyledDescriptionText>,
    },
    {
      title: "Email address",
      dataIndex: "email",
      key: "email",
      width: 190,
      ellipsis: true,
      render: (value) => <StyledDescriptionText>{value}</StyledDescriptionText>,
    },
    {
      title: "Offering type",
      dataIndex: "type",
      key: "type",
      width: 130,
      render: (value: OfferingTypeLabel) => (
        <Tag color={OFFERING_TYPE_TAG_COLORS[value]}>{value}</Tag>
      ),
    },
    {
      title: "Offering name",
      dataIndex: "offering",
      key: "offering",
      width: 180,
      ellipsis: true,
      render: (value) => <StyledDescriptionText>{value}</StyledDescriptionText>,
    },
    {
      title: "Purchase date",
      dataIndex: "date",
      key: "date",
      width: 170,
      render: (value) => (
        <StyledDescriptionText>
          {moment(value).format("ddd, MMM DD YYYY")}
        </StyledDescriptionText>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (value) => <StyledDescriptionText>{value}</StyledDescriptionText>,
    },
  ];

  if (error) {
    return (
      <Alert
        message="Sorry, something went wrong."
        description={error.message}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{width: "100%", overflowX: "auto"}}>
      <Table
        columns={columns}
        dataSource={tableData}
        loading={isLoading || isFetching}
        locale={{emptyText: "No usage history yet"}}
        scroll={{x: "max-content"}}
        pagination={{
          current: page,
          pageSize: perPage,
          total: data?.total ?? 0,
          position: ["bottomLeft"],
          showSizeChanger: false,
          onChange: (nextPage) => setPage(nextPage),
          itemRender: (pageNumber, type, originalElement) => {
            if (type === "prev") {
              return <Button size="small">Previous</Button>;
            }
            if (type === "next") {
              return <Button size="small">Next</Button>;
            }
            if (type === "page") {
              const isActive = pageNumber === page;
              return (
                <Button
                  size="small"
                  style={
                    isActive
                      ? {
                          backgroundColor: "#eae6f3",
                        }
                      : {}
                  }
                >
                  {pageNumber}
                </Button>
              );
            }
            return originalElement;
          },
        }}
      />
    </div>
  );
}
